import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [details, setDetails] = useState(null);
  const attempts = useRef(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      if (attempts.current >= 8) { setStatus("error"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/subscribe/status/${sessionId}`);
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.email) localStorage.setItem("ittv_sub_email", data.email.toLowerCase());
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <>
      <SEO title="Payment · ITTV News" description="Subscription confirmation" />
      <div className="container-editorial py-24 max-w-2xl mx-auto text-center" data-testid="subscribe-success">
        {status === "polling" && (
          <>
            <Loader2 className="mx-auto animate-spin text-[color:var(--accent)]" size={48} />
            <h1 className="font-serif text-4xl font-bold mt-6">Confirming payment…</h1>
            <p className="text-[color:var(--text-muted)] mt-3">This takes a few seconds. Please don&apos;t close the page.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-[color:var(--accent)]" size={56} />
            <div className="kicker text-[color:var(--accent)] mt-6 mb-2">Welcome, subscriber</div>
            <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight" data-testid="subscribe-success-title">You&apos;re in.</h1>
            <p className="text-lg text-[color:var(--text-muted)] mt-4">Your subscription is active. Julia&apos;s Weekly Long Read is now unlocked.</p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/category/opinion" className="btn-accent">Read the Long Read</Link>
              <Link to="/" className="btn-outline">Front page</Link>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="mx-auto text-[color:var(--accent)]" size={56} />
            <h1 className="font-serif text-4xl font-bold mt-6">Payment session expired</h1>
            <button onClick={() => nav("/subscribe")} className="btn-accent mt-6">Try again</button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-[color:var(--accent)]" size={56} />
            <h1 className="font-serif text-4xl font-bold mt-6">Could not confirm payment</h1>
            <p className="text-[color:var(--text-muted)] mt-3">If Stripe confirmed your payment, refresh in a minute. Otherwise you can retry.</p>
            <button onClick={() => nav("/subscribe")} className="btn-accent mt-6">Back to subscribe</button>
          </>
        )}
      </div>
    </>
  );
}
